import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "so_cart_v1";

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : { restaurantId: null, restaurantName: null, items: [] };
    } catch { return { restaurantId: null, restaurantName: null, items: [] }; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  const addItem = useCallback((restaurant, item) => {
    setCart((prev) => {
      // If switching restaurants, reset
      if (prev.restaurantId && prev.restaurantId !== restaurant.id) {
        return {
          restaurantId: restaurant.id,
          restaurantName: restaurant.name,
          items: [{ ...item, quantity: 1 }],
        };
      }
      const existing = prev.items.find((i) => i.id === item.id);
      const items = existing
        ? prev.items.map((i) => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i)
        : [...prev.items, { ...item, quantity: 1 }];
      return { restaurantId: restaurant.id, restaurantName: restaurant.name, items };
    });
  }, []);

  const updateQty = useCallback((itemId, delta) => {
    setCart((prev) => {
      const items = prev.items
        .map((i) => i.id === itemId ? { ...i, quantity: i.quantity + delta } : i)
        .filter((i) => i.quantity > 0);
      if (items.length === 0) return { restaurantId: null, restaurantName: null, items: [] };
      return { ...prev, items };
    });
  }, []);

  const clear = useCallback(() => setCart({ restaurantId: null, restaurantName: null, items: [] }), []);

  const subtotal = cart.items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = cart.items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addItem, updateQty, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
